import { createStyles, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import { booleanOptions, BooleanValue } from 'common/genericOptions';
import { EventoFinanceiro, TipoEventoFinanceiro } from 'common/genericTypes';
import UploadCsv from 'components/instrumentos/uploadCsv';
import { RadioButton, Section, SmSpace, Button } from 'm2m.components';
import React, { useEffect, useState } from 'react';
import { addAlert } from 'store/modules/snackBar';
import { connect } from 'react-redux';
import { RootState } from 'store';
import {
  updateAmortizacoes,
  updateDatasIncorporacaoJuros,
  updateDatasPagamentoJuros,
  updateField,
} from 'store/modules/cri/eventos';
import { v4 as uuidv4 } from 'uuid';
import { DatesSection } from './datesSection';
import { labelsEventosFinanceiros as labels } from 'common/registro/labels';
import { useValidarCampos, EtapaCadastro } from '../useValidarCampos';
import { PagamentoJurosSection } from './jurosSection';
import { AmortizacoesSection } from 'components/instrumentos/registro/eventosFinanceiros/amortizacoesSection';

interface EventosFinanceirosGroup {
  [key: string]: EventoFinanceiro[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    banner: {
      marginLeft: '-40px',
      padding: '40px',
      backgroundColor: `${theme.palette.primary.main}1A`,
    },
  })
);

const mapStateToProps = (state: RootState) => ({
  criteriosRemuneracao: state.criCadastro.criteriosRemuneracao,
  eventos: state.criCadastro.eventos,
  highlightErrors: state.criCadastro.highlightErrors,
});

const mapDispatchToProps = {
  updateDatasIncorporacaoJuros,
  updateDatasPagamentoJuros,
  updateField,
  updateAmortizacoes,
  addAlert,
};

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const Eventos: React.FC<Props> = (props) => {
  const {
    eventos,
    criteriosRemuneracao: { dataEmissao, dataVencimento, dataInicioRentabilidade },
    highlightErrors,
    updateField,
    updateDatasPagamentoJuros,
    updateAmortizacoes,
    updateDatasIncorporacaoJuros,
    addAlert,
  } = props;
  const classes = useStyles();

  const [groupedEventosFinanceiros, setGroupedEventosFinanceiros] = useState<EventosFinanceirosGroup>({});

  const camposInvalidos = useValidarCampos(EtapaCadastro.EventosFinanceiros);

  const showError = (label: string) => camposInvalidos.includes(label) && highlightErrors;

  useEffect(() => {
    if (groupedEventosFinanceiros[TipoEventoFinanceiro.Amortizacao]) {
      updateAmortizacoes(
        groupedEventosFinanceiros[TipoEventoFinanceiro.Amortizacao].map((amortizacao) => ({
          id: uuidv4(),
          percentualAmortizacao: amortizacao.percentual ?? undefined,
          dataAmortizacao: amortizacao.data,
        }))
      );
    }
    if (groupedEventosFinanceiros[TipoEventoFinanceiro.PagamentoJuros]) {
      updateDatasPagamentoJuros(
        groupedEventosFinanceiros[TipoEventoFinanceiro.PagamentoJuros].map((pagamento) => ({
          id: uuidv4(),
          date: pagamento.data,
        }))
      );
    }
    if (groupedEventosFinanceiros[TipoEventoFinanceiro.IncorporacaoJuros]) {
      updateDatasIncorporacaoJuros(
        groupedEventosFinanceiros[TipoEventoFinanceiro.IncorporacaoJuros].map((incorporacao) => ({
          id: uuidv4(),
          date: incorporacao.data,
        }))
      );
    }
  }, [groupedEventosFinanceiros, updateAmortizacoes, updateDatasIncorporacaoJuros, updateDatasPagamentoJuros]);

  const handleUploadCsv = (eventosFinanceiros: EventoFinanceiro[]) => {
    try {
      const groupedEventosFinanceiros = eventosFinanceiros.reduce<EventosFinanceirosGroup>((acc, curr) => {
        if (acc[curr.tipo]) {
          acc[curr.tipo].push(curr);
        } else {
          acc[curr.tipo] = [curr];
        }
        return acc;
      }, {});
      setGroupedEventosFinanceiros(groupedEventosFinanceiros);
    } catch (error) { }
  };

  const handleDeletarFluxoEventosFinanceiros = () => {
    updateAmortizacoes([]);
    updateDatasPagamentoJuros([]);
    updateDatasIncorporacaoJuros([]);
  };

  return (
    <div>
      <div className={classes.banner}>
        <Grid container spacing={4} alignItems="center">
          <Grid item md={4}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <AnnouncementIcon fontSize={'large'} />
              </Grid>
              <Grid item xs>
                <Typography variant="h3">{'Por que não agilizar esse processo?'}</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item container md={4} justify="center">
            <div>
              <Typography variant={'subtitle1'}>{'Upload de Eventos Financeiros'}</Typography>
              <SmSpace />
            </div>
            <UploadCsv
              onChange={handleUploadCsv}
              onError={(error) => addAlert({ severity: 'error', message: error })}
            />
          </Grid>
        </Grid>
      </div>

      <AmortizacoesSection
        percentualAmortizacaoSobre={eventos.percentualAmortizacaoSobre ?? ''}
        percentualAmortizacaoPassivelAlteracao={!!eventos.percentualAmortizacaoPassivelAlteracao}
        amortizacoes={eventos.amortizacoes ?? []}
        onUpdateField={updateField}
        onUpdateAmortizacoes={updateAmortizacoes}
        minDate={dataEmissao}
        maxDate={dataVencimento}
        useValidarCampos={useValidarCampos}
        shouldHighlightErrors={highlightErrors}
      />
      <PagamentoJurosSection
        onUpdateDates={updateDatasPagamentoJuros}
        dates={eventos.datasPagamentoJuros ?? []}
        minDate={dataInicioRentabilidade}
        maxDate={dataVencimento}
      />
      <DatesSection
        title={labels.incorporacaoJuros}
        label={labels.dataIncorporacaoJuros}
        onUpdateDates={updateDatasIncorporacaoJuros}
        dates={eventos.datasIncorporacaoJuros ?? []}
        minDate={dataInicioRentabilidade}
        maxDate={dataVencimento}
      />
      <Section title={'Resgate Antecipado Facultativo'}>
        <RadioButton
          label={labels.resgateAntecipadoFacultativo}
          options={booleanOptions}
          value={eventos.resgateAntecipadoFacultativo ? BooleanValue.Yes : BooleanValue.No}
          onChange={(resgateAntecipadoFacultativo: string) =>
            updateField({
              name: 'resgateAntecipadoFacultativo',
              value: resgateAntecipadoFacultativo === BooleanValue.Yes,
            })
          }
          inline
        />
      </Section>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Eventos);
